import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, ArrowsClockwise, Globe, UploadSimple, MagnifyingGlass, Broom } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  queued: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
  building: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  done: 'bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/30',
  failed: 'bg-red-500/10 text-red-400 border-red-500/30',
};

const HerokuExistingManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [jobApps, setJobApps] = useState({});   // app_name -> {status, buildpack, error}
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/heroku/accounts`, { withCredentials: true })
      .then(r => { setAccounts(r.data); if (r.data.length) setAccountId(r.data[0].id); })
      .catch(() => toast.error('Failed to load Heroku accounts'));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchApps = async () => {
    if (!accountId) return toast.error('Select a Heroku account');
    setLoadingApps(true); setApps([]); setSelected(new Set()); setJobApps({});
    try {
      const r = await axios.get(`${API}/api/heroku/existing/${accountId}/apps`, { withCredentials: true });
      setApps(r.data);
      toast.success(`Found ${r.data.length} existing apps`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch apps');
    } finally { setLoadingApps(false); }
  };

  const filtered = apps.filter(a => (a.name || '').toLowerCase().includes(search.toLowerCase()));

  const toggle = (name) => {
    const n = new Set(selected);
    n.has(name) ? n.delete(name) : n.add(name);
    setSelected(n);
  };
  const allSelected = filtered.length > 0 && filtered.every(a => selected.has(a.name));
  const toggleAll = () => {
    if (allSelected) { const n = new Set(selected); filtered.forEach(a => n.delete(a.name)); setSelected(n); }
    else { const n = new Set(selected); filtered.forEach(a => n.add(a.name)); setSelected(n); }
  };

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/heroku/existing/jobs/${jobId}`, { withCredentials: true });
        setJobApps(r.data.apps || {});
        if (r.data.finished) {
          clearInterval(pollRef.current);
          setRunning(false);
          const vals = Object.values(r.data.apps || {});
          const ok = vals.filter(v => v.status === 'done').length;
          const fail = vals.filter(v => v.status === 'failed').length;
          toast.success(`Override complete: ${ok} done, ${fail} failed`);
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
  };

  const runOverride = async () => {
    if (selected.size === 0) return toast.error('Select at least one app');
    if (!file) return toast.error('Upload a .zip file first');
    if (!window.confirm(`This will override ${selected.size} Heroku app(s) with your ZIP. This replaces their current deployment. Continue?`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('app_names', Array.from(selected).join(','));
      fd.append('file', file);
      const r = await axios.post(`${API}/api/heroku/existing/${accountId}/bulk-redeploy`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Override started for ${r.data.count} app(s)`);
      setRunning(true);
      const initial = {}; Array.from(selected).forEach(n => initial[n] = { status: 'queued' });
      setJobApps(initial);
      pollJob(r.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start override');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black text-[#79589F] mb-2">EXISTING URL MANAGER</h1>
        <p className="text-slate-400 font-mono">Fetch existing Heroku apps, select any number of them, and override them all with one ZIP.</p>
      </div>

      {/* Account + fetch */}
      <div className="bg-card border border-[#79589F]/30 p-6 max-w-5xl flex flex-col sm:flex-row gap-4 sm:items-end">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Heroku Account</label>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="existing-account-select"
            className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#79589F] appearance-none rounded-none">
            <option value="">-- Choose Account --</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>
        <button onClick={fetchApps} disabled={loadingApps || !accountId} data-testid="fetch-existing-apps-btn"
          className="flex items-center gap-2 bg-[#79589F] text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-[#6a4b8c] transition-colors disabled:opacity-50">
          {loadingApps ? <CircleNotch size={20} className="animate-spin" /> : <ArrowsClockwise size={20} />} Fetch Apps
        </button>
      </div>

      {/* Upload + bulk action */}
      {apps.length > 0 && (
        <div className="bg-card border border-[#79589F]/30 p-6 max-w-5xl flex flex-col lg:flex-row gap-4 lg:items-center">
          <label className="flex-1 flex items-center gap-3 border-2 border-dashed border-white/15 hover:border-[#79589F] transition-colors p-4 cursor-pointer">
            <UploadSimple size={24} className="text-[#a583f0] shrink-0" />
            <span className="text-sm text-slate-300 truncate">{file ? file.name : 'Click to select the .zip to deploy across selected apps'}</span>
            <input type="file" accept=".zip" className="hidden" data-testid="override-zip-input" onChange={(e) => setFile(e.target.files[0])} />
          </label>
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-400 font-mono">{selected.size} selected</div>
            <button onClick={runOverride} disabled={busy || running || selected.size === 0 || !file} data-testid="run-bulk-override-btn"
              className="flex items-center gap-2 bg-[#79589F] text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#6a4b8c] transition-colors disabled:opacity-40">
              {busy || running ? <CircleNotch size={20} className="animate-spin" /> : <Broom size={20} />}
              {running ? 'Overriding…' : 'Override Selected'}
            </button>
          </div>
        </div>
      )}

      {/* Apps table */}
      {apps.length > 0 && (
        <div className="bg-card border border-white/10 max-w-5xl">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-bold uppercase tracking-wider text-slate-200">{apps.length} Existing Apps</h3>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..."
                className="bg-[#0A0B0E] border border-white/10 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#79589F] w-56" />
            </div>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-black/20 sticky top-0">
                  <th className="p-4 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-checkbox" className="accent-[#79589F] w-4 h-4" /></th>
                  <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">App</th>
                  <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">URL</th>
                  <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const js = jobApps[a.name];
                  return (
                    <tr key={a.name} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4"><input type="checkbox" checked={selected.has(a.name)} onChange={() => toggle(a.name)} data-testid={`select-app-${a.name}`} className="accent-[#79589F] w-4 h-4" /></td>
                      <td className="p-4 font-mono font-bold text-white">{a.name}</td>
                      <td className="p-4">
                        <a href={a.web_url} target="_blank" rel="noreferrer" className="text-[#00E5FF] text-xs hover:underline flex items-center gap-1"><Globe size={13} /> {a.web_url}</a>
                      </td>
                      <td className="p-4">
                        {js ? (
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 text-xs font-bold tracking-wider border w-fit ${STATUS_STYLE[js.status] || 'bg-white/5 text-slate-300 border-white/10'}`}>{js.status}</span>
                            {js.status === 'done' && js.buildpack && <span className="text-[10px] text-slate-500 font-mono">{js.buildpack}</span>}
                            {js.status === 'failed' && <span className="text-[10px] text-red-400 font-mono max-w-xs truncate" title={js.error}>{js.error}</span>}
                          </div>
                        ) : <span className="text-slate-600 text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerokuExistingManager;
